import React, { useState, useEffect } from 'react';
import { datosCrudosAPI } from '../api';
import { useThemeTokens } from '../theme/ThemeProvider';

const DatosCrudos = () => {
  const { getColor, getSpacing, getFontSize, getFontWeight, getBorderRadius, getShadow, theme } = useThemeTokens();
  
  // Estados principales
  const [marketplaces, setMarketplaces] = useState([]);
  const [selectedMarketplace, setSelectedMarketplace] = useState('');
  const [marketplaceInfo, setMarketplaceInfo] = useState(null);
  
  // Estados de carga de archivo
  const [uploadedFile, setUploadedFile] = useState(null);
  const [fileValidation, setFileValidation] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Estados de análisis de columnas
  const [autoClean, setAutoClean] = useState(true);
  const [enableManualSelection, setEnableManualSelection] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [columnAnalysis, setColumnAnalysis] = useState(null);
  
  // Estados de UI
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [expandColumnOptions, setExpandColumnOptions] = useState(false);
  const [expandPreview, setExpandPreview] = useState(true);
  const [confirmSave, setConfirmSave] = useState(false);
  
  // Estados de progreso de guardado
  const [isSaving, setIsSaving] = useState(false);
  const [saveProgress, setSaveProgress] = useState(0);
  const [saveProgressMessage, setSaveProgressMessage] = useState('');

  const styles = {
    container: {
      padding: getSpacing('xl'),
      fontFamily: theme.typography.fontFamilies.primary,
      backgroundColor: getColor('neutral.lightGray'),
      minHeight: '100vh',
      color: getColor('neutral.black')
    },
    header: {
      marginBottom: getSpacing('xl')
    },
    title: {
      fontSize: getFontSize('3xl'),
      fontWeight: getFontWeight('bold'),
      color: getColor('neutral.black'),
      marginBottom: getSpacing('md')
    },
    subtitle: {
      fontSize: getFontSize('lg'),
      color: getColor('neutral.mediumGray')
    },
    card: {
      backgroundColor: getColor('neutral.white'),
      borderRadius: getBorderRadius('lg'),
      padding: getSpacing('xl'),
      marginBottom: getSpacing('lg'),
      boxShadow: getShadow('md')
    },
    sectionTitle: {
      fontSize: getFontSize('xl'),
      fontWeight: getFontWeight('semibold'),
      color: getColor('neutral.black'),
      marginBottom: getSpacing('md')
    },
    select: {
      width: '100%',
      padding: getSpacing('md'),
      fontSize: getFontSize('base'),
      borderRadius: getBorderRadius('md'),
      border: `1px solid ${getColor('neutral.mediumGray')}`,
      backgroundColor: getColor('neutral.white'),
      color: getColor('neutral.black'),
      marginBottom: getSpacing('md')
    },
    infoBox: {
      backgroundColor: getColor('neutral.lightGray'),
      padding: getSpacing('md'),
      borderRadius: getBorderRadius('md'),
      marginBottom: getSpacing('md')
    },
    infoLabel: {
      fontSize: getFontSize('sm'),
      fontWeight: getFontWeight('semibold'),
      color: getColor('neutral.darkGray'),
      marginRight: getSpacing('xs')
    },
    infoValue: {
      fontSize: getFontSize('sm'),
      color: getColor('neutral.black')
    },
    uploadZone: {
      border: `2px dashed ${dragActive ? getColor('primary.blue') : getColor('neutral.mediumGray')}`,
      borderRadius: getBorderRadius('lg'),
      padding: getSpacing('2xl'),
      textAlign: 'center',
      cursor: 'pointer',
      backgroundColor: getColor('neutral.lightGray'),
      transition: 'all 0.3s ease',
      marginBottom: getSpacing('md')
    },
    uploadText: {
      fontSize: getFontSize('lg'),
      color: getColor('neutral.darkGray'),
      marginBottom: getSpacing('xs')
    },
    uploadHint: {
      fontSize: getFontSize('sm'),
      color: getColor('neutral.mediumGray')
    },
    progressBar: {
      width: '100%',
      height: '8px',
      backgroundColor: getColor('neutral.lightGray'),
      borderRadius: getBorderRadius('sm'),
      overflow: 'hidden',
      marginTop: getSpacing('md')
    },
    progressFill: {
      height: '100%',
      backgroundColor: getColor('primary.blue'),
      transition: 'width 0.3s ease'
    },
    button: {
      padding: `${getSpacing('md')} ${getSpacing('xl')}`,
      fontSize: getFontSize('base'),
      fontWeight: getFontWeight('semibold'),
      borderRadius: getBorderRadius('md'),
      border: 'none',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      marginRight: getSpacing('sm')
    },
    primaryButton: {
      backgroundColor: getColor('primary.blue'),
      color: getColor('neutral.white')
    },
    secondaryButton: {
      backgroundColor: 'transparent',
      color: getColor('primary.blue'),
      border: `2px solid ${getColor('primary.blue')}`
    },
    dangerButton: {
      backgroundColor: getColor('states.error'),
      color: getColor('neutral.white')
    },
    checkbox: {
      marginRight: getSpacing('sm'),
      cursor: 'pointer'
    },
    label: {
      fontSize: getFontSize('base'),
      color: getColor('neutral.black'),
      marginBottom: getSpacing('xs'),
      display: 'flex',
      alignItems: 'center',
      cursor: 'pointer'
    },
    multiselect: {
      width: '100%',
      minHeight: '120px',
      padding: getSpacing('sm'),
      fontSize: getFontSize('sm'),
      borderRadius: getBorderRadius('md'),
      border: `1px solid ${getColor('neutral.mediumGray')}`,
      backgroundColor: getColor('neutral.white'),
      color: getColor('neutral.black')
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: getFontSize('sm'),
      marginTop: getSpacing('md')
    },
    tableHeader: {
      backgroundColor: getColor('neutral.lightGray'),
      padding: getSpacing('sm'),
      textAlign: 'left',
      fontSize: getFontSize('xs'),
      fontWeight: getFontWeight('semibold'),
      borderBottom: `1px solid ${getColor('neutral.mediumGray')}`,
      color: getColor('neutral.darkGray')
    },
    tableCell: {
      padding: getSpacing('sm'),
      borderBottom: `1px solid ${getColor('neutral.lightGray')}`,
      fontSize: getFontSize('xs'),
      maxWidth: '150px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      color: getColor('neutral.black')
    },
    alert: (type) => ({
      padding: getSpacing('md'),
      borderRadius: getBorderRadius('md'),
      marginBottom: getSpacing('md'),
      fontSize: getFontSize('sm'),
      backgroundColor: type === 'error' ? '#FEE' : type === 'success' ? '#E6FFED' : '#EBF8FF',
      color: type === 'error' ? '#C53030' : type === 'success' ? '#22543D' : '#2C5282',
      border: `1px solid ${type === 'error' ? '#FED7D7' : type === 'success' ? '#9AE6B4' : '#90CDF4'}`
    }),
    collapsible: {
      marginBottom: getSpacing('md')
    },
    collapsibleHeader: {
      padding: getSpacing('md'),
      backgroundColor: getColor('neutral.lightGray'),
      borderRadius: getBorderRadius('md'),
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontSize: getFontSize('base'),
      fontWeight: getFontWeight('medium'),
      color: getColor('neutral.black')
    },
    collapsibleContent: {
      padding: getSpacing('md'),
      backgroundColor: getColor('neutral.white'),
      borderRadius: `0 0 ${getBorderRadius('md')} ${getBorderRadius('md')}`,
      marginTop: '2px',
      border: `1px solid ${getColor('neutral.lightGray')}`
    },
    badge: {
      display: 'inline-block',
      padding: `${getSpacing('xs')} ${getSpacing('sm')}`,
      fontSize: getFontSize('xs'),
      fontWeight: getFontWeight('semibold'),
      borderRadius: getBorderRadius('full'),
      backgroundColor: getColor('primary.blue'),
      color: getColor('neutral.white'),
      marginLeft: getSpacing('xs')
    },
    removedColumnsList: {
      fontSize: getFontSize('sm'),
      color: getColor('neutral.mediumGray'),
      marginTop: getSpacing('sm')
    }
  };

  // Cargar marketplaces al montar
  useEffect(() => {
    loadMarketplaces();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cargar info cuando cambie el marketplace
  useEffect(() => {
    if (selectedMarketplace && !loading) {
      loadMarketplaceInfo(selectedMarketplace);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMarketplace]);

  const loadMarketplaces = async () => {
    try {
      setLoading(true);
      const response = await datosCrudosAPI.getMarketplaces();
      if (response.success) {
        setMarketplaces(response.data);
        if (response.data.length > 0) {
          setSelectedMarketplace(response.data[0]);
        }
      }
    } catch (err) {
      setError(`Error cargando marketplaces: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadMarketplaceInfo = async (marketplace) => {
    try {
      const response = await datosCrudosAPI.getMarketplaceInfo(marketplace);
      if (response.success) {
        setMarketplaceInfo(response.data);
      }
    } catch (err) {
      console.error('Error cargando info de marketplace:', err);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const handleFileUpload = async (file) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      setUploadProgress(0);
      setFileValidation(null);
      setColumnAnalysis(null);
      setConfirmSave(false);
      
      console.log('📤 Subiendo archivo:', file.name);
      
      const response = await datosCrudosAPI.uploadFile(
        file,
        selectedMarketplace,
        (progress) => setUploadProgress(progress)
      );

      console.log('✅ Respuesta del servidor:', response);

      if (response.success) {
        console.log('📊 Datos recibidos:', {
          session_id: response.data.session_id,
          num_rows: response.data.num_rows,
          num_columns: response.data.num_columns,
          columns: response.data.columns?.length,
          preview: response.data.preview?.length
        });
        
        setUploadedFile(file);
        setFileValidation(response.data);
        setSelectedColumns(response.data.columns);
        setSuccess(response.data.message);
        setExpandColumnOptions(true);
        setExpandPreview(true);
        
        console.log('✅ Estados actualizados - fileValidation establecido');
      } else {
        console.error('❌ Respuesta sin success=true:', response);
        setError('Error: respuesta del servidor sin success=true');
      }
    } catch (err) {
      console.error('❌ Error en handleFileUpload:', err);
      setError(`Error subiendo archivo: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  const handleAnalyzeColumns = async () => {
    if (!fileValidation) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await datosCrudosAPI.analyzeColumns(
        fileValidation.session_id,
        selectedMarketplace,
        autoClean,
        enableManualSelection ? selectedColumns : null
      );
      
      if (response.success) {
        setColumnAnalysis(response.data);
        setExpandPreview(true);
        setSuccess(`Análisis completado. ${response.data.num_columns_filtered} columnas finales.`);
      }
    } catch (err) {
      setError(`Error analizando columnas: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!fileValidation || !confirmSave) return;
    
    try {
      setIsSaving(true);
      setSaveProgress(0);
      setSaveProgressMessage('Iniciando...');
      setError(null);
      setSuccess(null);
      
      console.log('🚀 Iniciando guardado con progreso en tiempo real...');
      
      // Construir URL para EventSource
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const token = localStorage.getItem('authToken');  // Corregido: authToken no token
      
      if (!token) {
        setError('Error: No se encontró token de autenticación. Por favor, inicie sesión nuevamente.');
        setIsSaving(false);
        return;
      }
      
      // EventSource no soporta headers personalizados, así que pasamos el token como query param
      const url = `${baseURL}/datos-crudos/save-with-progress?session_id=${fileValidation.session_id}&marketplace=${selectedMarketplace}&fecha=${fileValidation.fecha}&confirmed=true&token=${token}`;
      
      console.log('📡 Conectando a:', url.replace(token, '***TOKEN***'));
      
      const eventSource = new EventSource(url);
      
      // Timeout de seguridad (30 minutos para archivos grandes)
      const timeoutId = setTimeout(() => {
        console.warn('⏱️ Timeout alcanzado después de 30 minutos');
        eventSource.close();
        setIsSaving(false);
        setError('La operación está tomando más tiempo de lo esperado. Verifique el estado en la base de datos.');
      }, 30 * 60 * 1000);
      
      eventSource.onmessage = (event) => {
        console.log('📨 Evento recibido:', event.data);
        
        try {
          const data = JSON.parse(event.data);
          setSaveProgress(data.progress || 0);
          setSaveProgressMessage(data.message || '');
          
          // Si completó (éxito o error)
          if (data.success === true) {
            console.log('✅ Guardado completado exitosamente');
            clearTimeout(timeoutId);
            eventSource.close();
            setIsSaving(false);
            setSuccess(`✅ Dataset guardado exitosamente: ${data.num_rows?.toLocaleString()} registros`);
            // Recargar info de marketplace
            loadMarketplaceInfo(selectedMarketplace);
            // Limpiar estado
            resetUpload();
          } else if (data.success === false) {
            console.error('❌ Error en guardado:', data.message);
            clearTimeout(timeoutId);
            eventSource.close();
            setIsSaving(false);
            setError(data.message || 'Error al guardar dataset');
          }
        } catch (parseErr) {
          console.error('Error parseando evento:', parseErr);
        }
      };
      
      eventSource.onerror = (error) => {
        console.error('❌ Error en EventSource:', error);
        clearTimeout(timeoutId);
        eventSource.close();
        setIsSaving(false);
        setError('Error de conexión durante el guardado. Por favor, intente nuevamente.');
      };
      
      // Cleanup al desmontar
      return () => {
        clearTimeout(timeoutId);
        if (eventSource.readyState !== EventSource.CLOSED) {
          eventSource.close();
        }
      };
      
    } catch (err) {
      console.error('❌ Error en handleSave:', err);
      setError(`Error: ${err.message}`);
      setIsSaving(false);
    }
  };

  const handleCleanupOrphans = async () => {
    if (!window.confirm('¿Está seguro de que desea limpiar metadatos huérfanos?')) return;
    
    try {
      setLoading(true);
      const response = await datosCrudosAPI.cleanupOrphans();
      if (response.success) {
        setSuccess(response.message);
        await loadMarketplaceInfo(selectedMarketplace);
      }
    } catch (err) {
      setError(`Error limpiando metadatos: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const resetUpload = () => {
    setUploadedFile(null);
    setFileValidation(null);
    setColumnAnalysis(null);
    setSelectedColumns([]);
    setAutoClean(true);
    setEnableManualSelection(false);
    setConfirmSave(false);
    setExpandColumnOptions(false);
    setExpandPreview(true);
  };

  const handleColumnSelectionChange = (e) => {
    const options = e.target.options;
    const selected = [];
    for (let i = 0; i < options.length; i++) {
      if (options[i].selected) {
        selected.push(options[i].value);
      }
    }
    setSelectedColumns(selected);
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>📊 Datos Crudos</h1>
        <p style={styles.subtitle}>
          Cargar, verificar y administrar bases de datos concatenadas por marketplace
        </p>
      </div>

      {/* Alertas */}
      {error && (
        <div style={styles.alert('error')}>
          ❌ {error}
        </div>
      )}
      {success && (
        <div style={styles.alert('success')}>
          ✅ {success}
        </div>
      )}

      {/* Selección de Marketplace */}
      <div style={styles.card}>
        <h2 style={styles.sectionTitle}>1. Seleccionar Marketplace</h2>
        
        <select
          style={styles.select}
          value={selectedMarketplace}
          onChange={(e) => setSelectedMarketplace(e.target.value)}
          disabled={loading}
        >
          {marketplaces.map((mp) => (
            <option key={mp} value={mp}>
              {mp.toUpperCase()}
            </option>
          ))}
        </select>

        {marketplaceInfo && marketplaceInfo.existe && (
          <div style={styles.infoBox}>
            <div style={{marginBottom: getSpacing('xs')}}>
              <span style={styles.infoLabel}>📋 Estado:</span>
              <span style={styles.infoValue}>Datos existentes</span>
            </div>
            <div style={{marginBottom: getSpacing('xs')}}>
              <span style={styles.infoLabel}>📊 Registros:</span>
              <span style={styles.infoValue}>{marketplaceInfo.num_filas?.toLocaleString()}</span>
            </div>
            <div style={{marginBottom: getSpacing('xs')}}>
              <span style={styles.infoLabel}>🔢 Versión:</span>
              <span style={styles.infoValue}>#{marketplaceInfo.version_numero}</span>
            </div>
            <div style={{marginBottom: getSpacing('xs')}}>
              <span style={styles.infoLabel}>📅 Fecha:</span>
              <span style={styles.infoValue}>{marketplaceInfo.fecha}</span>
            </div>
            {marketplaceInfo.tabla_backup_existe && (
              <div>
                <span style={styles.infoLabel}>💾 Backup:</span>
                <span style={styles.infoValue}>Disponible</span>
              </div>
            )}
          </div>
        )}

        {marketplaceInfo && !marketplaceInfo.existe && (
          <div style={styles.infoBox}>
            <span style={styles.infoValue}>
              ℹ️ No existe un concatenado para {selectedMarketplace.toUpperCase()}. Puede crear uno nuevo.
            </span>
          </div>
        )}
      </div>

      {/* Subir Archivo */}
      <div style={styles.card}>
        <h2 style={styles.sectionTitle}>2. Subir Archivo</h2>
        
        <div
          style={styles.uploadZone}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => document.getElementById('file-input').click()}
        >
          <input
            id="file-input"
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={handleFileInput}
            style={{ display: 'none' }}
          />
          <p style={styles.uploadText}>
            {uploadedFile ? `📄 ${uploadedFile.name}` : '📁 Arrastra tu archivo aquí o haz clic para seleccionar'}
          </p>
          <p style={styles.uploadHint}>
            Soporta CSV y Excel (.csv, .xlsx, .xls)
          </p>
        </div>

        {uploadProgress > 0 && uploadProgress < 100 && (
          <div>
            <p style={{...styles.uploadHint, marginBottom: getSpacing('sm')}}>
              Subiendo... {uploadProgress}%
            </p>
            <div style={styles.progressBar}>
              <div style={{...styles.progressFill, width: `${uploadProgress}%`}} />
            </div>
          </div>
        )}

        {fileValidation && (
          <div style={styles.infoBox}>
            <div style={{marginBottom: getSpacing('xs')}}>
              <span style={styles.infoLabel}>✅ Archivo cargado:</span>
              <span style={styles.infoValue}>{fileValidation.filename}</span>
            </div>
            <div style={{marginBottom: getSpacing('xs')}}>
              <span style={styles.infoLabel}>📊 Filas:</span>
              <span style={styles.infoValue}>{fileValidation.num_rows.toLocaleString()}</span>
              <span style={{...styles.infoLabel, marginLeft: getSpacing('md')}}>📋 Columnas:</span>
              <span style={styles.infoValue}>{fileValidation.num_columns}</span>
            </div>
            <div style={{marginBottom: getSpacing('xs')}}>
              <span style={styles.infoLabel}>🔤 Encoding:</span>
              <span style={styles.infoValue}>{fileValidation.encoding_detected}</span>
              <span style={{...styles.infoLabel, marginLeft: getSpacing('md')}}>🔗 Delimitador:</span>
              <span style={styles.infoValue}>{fileValidation.delimiter_detected}</span>
            </div>
            <div>
              <span style={styles.infoLabel}>📅 Fecha inferida:</span>
              <span style={styles.infoValue}>{fileValidation.fecha}</span>
            </div>
          </div>
        )}
      </div>

      {/* Opciones de Limpieza de Columnas */}
      {fileValidation && (
        <div style={styles.card}>
          <div style={styles.collapsible}>
            <div
              style={styles.collapsibleHeader}
              onClick={() => setExpandColumnOptions(!expandColumnOptions)}
            >
              <span>3. Opciones de Limpieza de Columnas</span>
              <span>{expandColumnOptions ? '▼' : '▶'}</span>
            </div>
            
            {expandColumnOptions && (
              <div style={styles.collapsibleContent}>
                <div style={{marginBottom: getSpacing('md')}}>
                  <label style={styles.label}>
                    <input
                      type="checkbox"
                      checked={autoClean}
                      onChange={(e) => setAutoClean(e.target.checked)}
                      style={styles.checkbox}
                    />
                    Eliminar columnas basura automáticamente
                  </label>
                  <p style={{...styles.uploadHint, marginLeft: getSpacing('xl')}}>
                    Elimina columnas con muchos nulos, constantes, "unnamed", etc.
                  </p>
                </div>

                <div style={{marginBottom: getSpacing('md')}}>
                  <label style={styles.label}>
                    <input
                      type="checkbox"
                      checked={enableManualSelection}
                      onChange={(e) => setEnableManualSelection(e.target.checked)}
                      style={styles.checkbox}
                    />
                    Seleccionar columnas manualmente
                  </label>
                </div>

                {enableManualSelection && (
                  <div style={{marginBottom: getSpacing('md')}}>
                    <p style={{...styles.uploadHint, marginBottom: getSpacing('xs')}}>
                      Seleccione las columnas a conservar (Ctrl+Click para múltiples):
                    </p>
                    <select
                      multiple
                      style={styles.multiselect}
                      value={selectedColumns}
                      onChange={handleColumnSelectionChange}
                    >
                      {fileValidation.columns.map((col) => (
                        <option key={col} value={col}>
                          {col}
                        </option>
                      ))}
                    </select>
                    <p style={{...styles.uploadHint, marginTop: getSpacing('xs')}}>
                      {selectedColumns.length} columnas seleccionadas
                    </p>
                  </div>
                )}

                <button
                  style={{...styles.button, ...styles.primaryButton}}
                  onClick={handleAnalyzeColumns}
                  disabled={loading}
                >
                  {loading ? 'Analizando...' : 'Aplicar Filtros'}
                </button>
              </div>
            )}
          </div>

          {columnAnalysis && (
            <div style={{...styles.infoBox, marginTop: getSpacing('md')}}>
              <div style={{marginBottom: getSpacing('xs')}}>
                <span style={styles.infoLabel}>📊 Columnas originales:</span>
                <span style={styles.infoValue}>{columnAnalysis.num_columns_original}</span>
                <span style={{...styles.infoLabel, marginLeft: getSpacing('md')}}>✅ Columnas finales:</span>
                <span style={styles.infoValue}>{columnAnalysis.num_columns_filtered}</span>
              </div>
              
              {columnAnalysis.removed_columns.length > 0 && (
                <div>
                  <span style={styles.infoLabel}>🗑️ Columnas eliminadas:</span>
                  <span style={styles.badge}>{columnAnalysis.removed_columns.length}</span>
                  <div style={styles.removedColumnsList}>
                    {columnAnalysis.removed_columns.map((col) => (
                      <div key={col}>
                        • {col} - <em>({columnAnalysis.removal_reasons[col]})</em>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Vista Previa */}
      {fileValidation && (
        <div style={styles.card}>
          <div style={styles.collapsible}>
            <div
              style={styles.collapsibleHeader}
              onClick={() => setExpandPreview(!expandPreview)}
            >
              <span>4. Vista Previa de Datos</span>
              <span>{expandPreview ? '▼' : '▶'}</span>
              </div>

            {expandPreview && (
              <div style={styles.collapsibleContent}>
              <div style={{ overflowX: 'auto', maxHeight: '500px', overflowY: 'auto' }}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                        {(columnAnalysis?.preview || fileValidation?.preview)?.[0] &&
                          Object.keys((columnAnalysis?.preview || fileValidation?.preview)[0]).map((col, idx) => (
                            <th key={idx} style={styles.tableHeader}>
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                      {(columnAnalysis?.preview || fileValidation?.preview)?.map((row, idx) => (
                        <tr key={idx}>
                          {Object.values(row).map((cell, cellIdx) => (
                            <td key={cellIdx} style={styles.tableCell} title={String(cell)}>
                              {String(cell)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
                <p style={{...styles.uploadHint, marginTop: getSpacing('md')}}>
                  Mostrando primeras 10 filas
                  {columnAnalysis && ' (después de aplicar filtros)'}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Confirmación y Guardado */}
      {fileValidation && (
        <div style={styles.card}>
          <h2 style={styles.sectionTitle}>5. Confirmar y Guardar</h2>
          
          <div style={{marginBottom: getSpacing('md')}}>
            <label style={styles.label}>
              <input
                type="checkbox"
                checked={confirmSave}
                onChange={(e) => setConfirmSave(e.target.checked)}
                style={styles.checkbox}
              />
              Confirmo que estos datos son correctos y deseo cargarlos a la base de datos
            </label>
          </div>

          {marketplaceInfo && marketplaceInfo.existe && (
            <div style={{...styles.alert('info'), marginBottom: getSpacing('md')}}>
              ⚠️ Al guardar, la versión actual se moverá a backup y se creará una nueva versión.
            </div>
          )}

          <button
            style={{...styles.button, ...styles.primaryButton}}
            onClick={handleSave}
            disabled={!confirmSave || isSaving}
          >
            {isSaving ? 'Guardando...' : '💾 Guardar en Base de Datos'}
          </button>

          <button
            style={{...styles.button, ...styles.secondaryButton}}
            onClick={resetUpload}
            disabled={isSaving}
          >
            🔄 Cancelar y Limpiar
          </button>
          
          {/* Barra de progreso durante el guardado */}
          {isSaving && (
            <div style={{marginTop: getSpacing('lg')}}>
              <p style={{...styles.uploadHint, marginBottom: getSpacing('sm'), fontWeight: getFontWeight('semibold')}}>
                {saveProgressMessage} - {saveProgress}%
              </p>
              <div style={styles.progressBar}>
                <div style={{...styles.progressFill, width: `${saveProgress}%`}} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Herramientas de Administración */}
      <div style={styles.card}>
        <h2 style={styles.sectionTitle}>🔧 Herramientas de Administración</h2>
        
        <button
          style={{...styles.button, ...styles.dangerButton}}
          onClick={handleCleanupOrphans}
          disabled={loading}
        >
          🧹 Limpiar Metadatos Huérfanos
        </button>
        
        <p style={{...styles.uploadHint, marginTop: getSpacing('sm')}}>
          Elimina registros de catálogo sin tablas de datos reales
        </p>
      </div>
    </div>
  );
};

export default DatosCrudos;
